<template>
  <div
    @click="handleOverlayClick"
    @keydown.esc="handleOverlayClick"
  >
    <transition
      enter-class="opacity-0"
      enter-active-class="ease-out duration-300 transition"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-active-class="transition ease-in duration-300"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 transition-opacity w-full h-full"
        style="z-index: 9999999997"
      >
        <div class="absolute inset-0 bg-neutral-900 opacity-75" />
      </div>
    </transition>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="fixed bottom-0 inset-x-0 px-4 pb-6 sm:inset-0 sm:p-0 sm:flex sm:items-center sm:justify-center"
        style="z-index: 9999999998"
      >
        <div
          ref="section"
          class="bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transition-all sm:max-w-lg sm:w-full sm:p-6 relative"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div>
            <button
              :aria-label="$t('common.cancel')"
              class="absolute top-6 right-6 focus:outline-none"
              type="button"
              @click="cancel"
            >
              <fa
                :icon="['fal', 'times']"
                class="text-2xl text-neutral-400 hover:text-neutral-300 transition duration-250 ease-in-out"
              />
            </button>
            <h3
              id="modal-headline"
              class="font-title text-lg leading-6 font-medium text-neutral-900"
            >
              {{ $t('quote.booking-summary') }}
            </h3>

            <div class="flex items-center justify-start mt-5 *:text-green-700 space-x-4 py-1">
              <fa
                :icon="['fal', 'circle-check']"
                class="text-3xl"
              />
              <p>
                {{ $t('quote.your-date-and-timeslot-is-available') }}
              </p>
            </div>

            <div class="mt-6 bg-ocean-100 p-4 rounded-md">
              <div class="text-lg text-neutral-700">
                {{ getAppointmentDate(quote.start_at, timezone, true) }}
              </div>
              <div class="text-neutral-700 text-sm">
                {{ getAppointmentTimeRange(quote, timezone, true) }}
                <span class="font-light">({{ timezone }})</span>
              </div>
            </div>

            <div class="mt-8 space-y-3">
              <div
                v-for="(item, index) in quote.items"
                :key="index"
                class="flex items-start justify-between"
              >
                <div class="font-light flex items-center justify-start">
                  {{ $t(`quote.item-type.${item.type}`) }}
                  <fa
                    v-if="item.type === 'BOOKING_FEE'"
                    v-tippy
                    :content="
                      $t(
                        'quote.top-notch-customer-support-and-secure-booking-experience',
                      )
                    "
                    :icon="['fas', 'question-circle']"
                    class="text-lg text-ocean-700 ml-2"
                  />
                </div>
                <div class="font-light">
                  {{
                    $n(formatMoney(convertCurrency(item.amount.amount)), {
                      style: 'currency',
                      currency: getConversionCurrency || item.amount.currency,
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })
                  }}
                </div>
              </div>
              <div class="flex items-start justify-between">
                <div class="font-light">
                  {{ $t('app.tax') }}
                </div>
                <div class="font-light">
                  {{
                    $n(formatMoney(convertCurrency(quote.tax && quote.tax.amount) || 0), {
                      style: 'currency',
                      currency: getConversionCurrency || quote.tax.currency,
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="quote.wallet"
              class="mt-6 pt-6 border-t border-dashed border-neutral-300"
            >
              <div class="flex items-start justify-between">
                <div class="font-light">
                  Wallet Balance
                </div>
                <div class="text-green-700">
                  <fa
                    :icon="['fas', 'wallet']"
                    class="inline-flex items-center justify-end text-lg mr-2"
                  />
                  {{
                    $n(formatMoneyRounded(convertCurrency(quote.wallet_amount.amount)), {
                      style: 'currency',
                      currency: getConversionCurrency || quote.wallet_amount.currency,
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })
                  }}
                </div>
              </div>
            </div>

            <div class="my-6 space-y-3 border-t border-b border-dashed border-neutral-300 py-6">
              <div class="flex items-start justify-between text-lg">
                <div class="font-bold">
                  {{ $t('app.total') }}
                </div>
                <div class="font-bold">
                  <vetster-discount slot-class="text-sm font-normal">
                    {{
                      $n(formatMoney(convertCurrency(quote.total.amount)), {
                        style: 'currency',
                        currency: getConversionCurrency || quote.total.currency,
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })
                    }}
                  </vetster-discount>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-6">
            <span class="flex w-full rounded-md shadow-sm">
              <vetster-button
                variant="bubblegum"
                class="inline-flex justify-center w-full"
                @click="agree"
              >
                {{ $t('quote.continue-booking') }}
              </vetster-button>
            </span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import {
  faQuestionCircle as fasQuestionCircle,
  faWallet,
} from '@fortawesome/pro-solid-svg-icons';

import VetsterDiscount from '@/components/VetsterDiscount';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import {
  formatMoney,
  formatMoneyRounded,
} from '@/helpers/format';
import ClickAway from '@/mixins/ClickAway';
import CloseOnEscape from '@/mixins/CloseOnEscape';
import HasConversionRate from '@/mixins/HasConversionRate';

library.add({
  faCheck,
  faTimes,
  fasQuestionCircle,
  faWallet,
});

export default {
  name: 'VetsterQuoteDetailsModal',

  components: { VetsterDiscount },

  mixins: [
    ClickAway,
    CloseOnEscape,
    HasConversionRate,
  ],

  props: {
    quote: {
      type: Object,
      default: () => ({}),
    },
    timezone: {
      type: String,
      default: '',
    },
    disableClickOutside: {
      default: false,
      type: Boolean,
    },
    disableCloseOnEscape: {
      default: false,
      type: Boolean,
    },
  },

  setup() {
    const {
      getAppointmentDate,
      getAppointmentTimeRange,
    } = useAppointmentDatetimeFormat();

    return {
      getAppointmentDate,
      getAppointmentTimeRange,
    };
  },

  data() {
    return {
      isOpen: false,
      resolve: () => {},
      reject: () => {},
    };
  },

  methods: {
    closeOnEscape(callback) {
      if (!this.disableCloseOnEscape) {
        this.escapeHandler = (event) => {
          if (event.key === 'Escape') {
            callback();
          }
        };

        document.addEventListener('keydown', this.escapeHandler);
      }
    },

    formatMoney,
    formatMoneyRounded,

    handleOverlayClick(event) {
      if (!this.disableClickOutside) {
        this.hideOnClickAway(
          event,
          this.$refs.section,
          this.cancel,
          this.$refs.contents,
        );
      }
    },

    open() {
      this.closeOnEscape(this.cancel);
      this.$emit('open');
      this.isOpen = true;

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },

    agree() {
      this.resolve(true);
    },

    cancel() {
      this.resolve(false);
      this.isOpen = false;
      this.$emit('close');
      this.destroyCloseOnEscape();
    },
  },
};
</script>
